import * as ProfileTypes from '../profileTypes';

/*
 * Enums for form data
 */
export enum SignatureType {
    "CITIZEN" = "CITIZEN",
    "WITNESS" = "WITNESS",
    "FIRST_REPRESENTATIVE" = "FIRST_REPRESENTATIVE",
    "SECOND_REPRESENTATIVE" = "SECOND_REPRESENTATIVE"
}

// Note - this differs from our own ProfileTypes.Race, because the DFCS form only includes these specific options
export enum Race {
    "American Indian or Alaska Native" = "AI",
    "Asian" = "AS",
    "Black or African American" = "BL",
    "Native Hawaiian or Pacific Islander" = "HP",
    "White" = "WH",
    "Other" = "OTHER",
}
  
export enum Relationship {
    "Sibling" = "SIBLING",
    "Parent" = "PARENT",
    "Partner" = "PARTNER",
    "Child" = "CHILD",
    "Dependent" = "DEPENDENT",
}
  
export enum PaymentFrequency {
    "Weekly" = "WEEKLY",
    "Bi-weekly" = "BI_WEEKLY",
    "Monthly" = "MONTHLY",
    "Semi-monthly" = "SEMI_MONTHLY",
}
  
export enum AssistanceFrequency {
    "One time" = "ONE_TIME",
    "Ongoing" = "ONGOING",
}
  
/*
* Form data
*/
export interface FormData {
    selectedBenefits: {
        foodStamps: boolean;
        TANF: boolean;
        GRG: boolean;
        refugeeCashAssistance: boolean;
        medicaid: boolean;
    }

    applicant: Applicant;
    householdMembers: HouseholdMember[];

    authorizedRepresentatives: {
        first?: AuthorizedRepresentative;
        second?: AuthorizedRepresentative;
        toReceiveMedicaidCard?: boolean;
        requiredAssistance?: RequiredAssistance;
    };

    expeditedFoodStampQualification?: ExpeditedFoodStampQualification;
    additionalQualifications: AdditionalQualifications;

    income: Income[];

    expenses: {
        careExpenses: CareExpense[];

        hasDependentTransportationExpenses: boolean;
        areDependentTransportationExpensesIncluded?: boolean;
        totalMilesDrivenWeekly?: number; // >= 0

        medicalExpenses: MedicalExpense[];
        medicalTransportationExpenses: MedicalTransportationExpense[];
        childSupportExpenses: ChildSupportExpense[];

        shelterOrUtilityExpenses?: ShelterOrUtilityExpenses;
        sharedExpenses?: SharedExpenses;
        otherBillPayer?: OtherBillPayer;
    }

    voterRegistrationIntent?: boolean;

    equifaxConsent: boolean;
}

/*
* Interfaces used in form data
*/

// Assistance
export interface RequiredAssistance {
    signLanguageInterpreter: boolean;
    TTY: boolean;
    largePrint: boolean;
    email: boolean;
    braille: boolean;
    videoRelay: boolean;
    cuedSpeechInterpreter: boolean;
    oralInterpreter: boolean;
    tactileInterpreter: boolean;
    telephoneCallReminder: boolean;
    telephonicSignature: boolean;
    faceToFaceInterview: boolean;
    otherInfo: string;

    frequency: AssistanceFrequency;
    whenAndHowLongInfo: string;
}

// People
export interface Person {
    firstName: string; // Not empty
    middleInitial: string;
    lastName: string; // Not empty

    isApplyingForBenefits: boolean;

    dateOfBirth: Date;
    sex: ProfileTypes.Sex;

    socialSecurityNumber?: string;
    isUSCitizenOrQualifiedImmigrant?: boolean;
}

export interface HouseholdMember extends Person {
    relationship: Relationship;
    race: Race;
    isHispanicOrLatino?: boolean;
}

export interface Applicant extends Person {
    suffix: string;

    useCareProviderAddressForStreet: boolean;
    streetAddress?: ProfileTypes.Address;

    useCareProviderAddressForMailing: boolean;
    mailingAddress?: ProfileTypes.Address;

    mainPhone: string;
    otherPhone: string;
    email: string;

    emailCommunicationPreferred: boolean;
    smsCommunicationPreferred: boolean;

    preferredLanguage: string; // Not empty
    interpreterNeeded: boolean;
    requiredAssistance?: RequiredAssistance;
}

export interface AuthorizedRepresentative {
    name: string; // Not empty
    phone: string;
    address: ProfileTypes.Address;
    preferredLanguage: string; // Not empty
    interpreterNeeded: boolean;
}

// Qualifications
export interface ExpeditedFoodStampQualification {
    isMigrantOrSeasonalFarmWorker: boolean;
    whoMigrantOrSeasonalFarmWorker?: string;

    totalEarnedIncomeThisMonth: number;
    employerName?: string;
    employmentBeginDate?: string;
    employmentEndDate?: string;
    employmentPayRate?: number; // >= 0
    employmentHoursPerWeek?: number; // >= 0
    earnedIncomeFrequency: PaymentFrequency;

    totalUnearnedIncomeThisMonth: number; // >= 0

    firstUnearnedIncomeType?: string;
    firstUnearnedIncomeAmount?: number; // >= 0
    firstUnearnedIncomeFrequency?: PaymentFrequency;

    secondUnearnedIncomeType?: string;
    secondUnearnedIncomeAmount?: number; // >= 0
    secondUnearnedIncomeFrequency?: PaymentFrequency;

    totalIncomeThisMonth: number; // >= 0

    totalMoneyInCashAndBank: number; // >= 0

    totalRentOrMortgagePayment: number; // >= 0

    totalUtilitiesPayment: number; // >= 0
    hasElectricUtilities: boolean;
    hasGasUtilities: boolean;
    hasAirConUtilities: boolean;
    hasKeroseneUtilities: boolean;
    hasWoodUtilities: boolean;
    hasReceivedEnergyAssistance: boolean;
    energyAssistanceAmount?: number; // >= 0
}

export interface AdditionalQualifications {
    hasBenefitsFromOtherCountyOrState: boolean;
    whoBenefitsFromOtherCountyOrState?: string;
    whereBenefitsFromOtherCountyOrState?: string;
    whenBenefitsFromOtherCountyOrState?: string;

    hasFalseInformationBenefitsConvicion?: boolean;
    whoFalseInformationBenefitsConvicion?: string;
    whereFalseInformationBenefitsConvicion?: string;
    whenFalseInformationBenefitsConvicion?: string;

    hasVoluntarilyQuitOrReducedHours?: boolean;
    whoVoluntarilyQuitOrReducedHours?: string;
    whyVoluntarilyQuitOrReducedHours?: string;

    isPregnant?: boolean;
    whoPregnant?: string;
    pregnancyDueDate?: string;

    hasUnpaidMedicalBills?: boolean;

    hasFoodStampOrTANFDisqualification?: boolean;
    whoFoodStampOrTANFDisqualification?: string;
    whereFoodStampOrTANFDisqualification?: string;

    isAvoidingProsecutionOrJail?: boolean;
    whoAvoidingProsecutionOrJail?: string;

    hasParoleViolation?: boolean;
    whoParoleVoliation?: string;

    hasFelonyDrugOrViolenceConviction?: boolean;
    whoFelonyDrugOrViolenceConviction?: string;
    whenFelonyDrugOrViolenceConviction?: string;
    isComplyingWithFelonyDrugOrViolenceConvictionProbationTerms?: boolean;
    isComplyingWithFelonyDrugOrViolenceConvictionParoleTerms?: boolean;
    hasCompletedAllFelonyDrugOrViolenceConvictionTerms?: boolean;

    hasFoodStampDrugTradingConviction?: boolean;
    whoFoodStampDrugTradingConviction?: string;
    whenFoodStampDrugTradingConviction?: string;

    hasFoodStampSellingConviction?: boolean;
    whoFoodStampSellingConviction?: string;
    whenFoodStampSellingConviction?: string;

    hasFoodStampWeaponsTradingConviction?: boolean;
    whoFoodStampWeaponsTradingConviction?: string;
    whenFoodStampWeaponsTradingConviction?: string;

    hasGamblingWinnings?: boolean;
    whoGamblingWinnings?: string;
    whenGamblingWinnings?: string;
    gamblingWinningsAmount?: number; // >= 0

    hasMisusedTANF?: boolean;
    whoMisusedTANF?: string;
    whenMisusedTANF?: string;
}

// Income
export interface Income {
    name: string; // Not empty
    type: string; // Not empty
    source: string; // Not empty
    monthlyAmount: number; // >= 0
    frequency: PaymentFrequency;
    hourlyRate: number; // >= 0
    hoursPerWeek: number; // >= 0
    datesPaid: string; // Not empty
}

// Expenses
export interface CareExpense {
    recipientName: string; // Not empty
    payerName: string; // Not empty
    reason: string; // Not empty
    provider: string; // Not empty
    amount: number; // >= 0
    frequency: PaymentFrequency;
}

export interface MedicalExpense {
    name: string; // Not empty
    type: string; // Not empty
    amount: number; // >= 0
    stillOwed: boolean;
    datePaid: string;
    willInsurancePay: boolean;
}

export interface MedicalTransportationExpense {
    purpose: string; // Not empty
    milesDriven: number; // >= 0
    cost: number; // >= 0
}

export interface ChildSupportExpense {
    payerName: string; // Not empty
    childName: string; // Not empty
    obligatedAmount: number; // >= 0
    actualAmount: number; // >= 0
    recipientName: string; // Not empty
}

export interface ShelterOrUtilityExpense {
    amount: number; // >= 0
    frequency: PaymentFrequency;
    payerName: string; // Not empty
}

export interface ShelterOrUtilityExpenses {
    rentOrMortgage?: ShelterOrUtilityExpense;
    propertyTax?: ShelterOrUtilityExpense;
    propertyInsurance?: ShelterOrUtilityExpense;
    electricity?: ShelterOrUtilityExpense;
    gas?: ShelterOrUtilityExpense;
    garbage?: ShelterOrUtilityExpense;
    telephone?: ShelterOrUtilityExpense;
    other?: ShelterOrUtilityExpense;
}

export interface SharedExpenses {
    who: string; // Not empty
    comments: string;
    recipientName: string; // Not empty
    paymentAmount: number; // >= 0
    paymentFrequency: PaymentFrequency;
    landlordName: string; // Not empty
    landlordAddress: ProfileTypes.Address;
}

export interface OtherBillPayer {
    payerName: string; // Not empty
    whichBills: string; // Not empty
    amount: string; // Not empty
    recipientName: string; // Not empty
}